import type { ReactNode } from 'react';
import type { HintView } from '../game/state.ts';
import { CheckIcon, CloseIcon, EraseIcon, HintIcon, PencilIcon, RedoIcon, UndoIcon } from './Icons.tsx';

interface ToolProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  busy?: boolean;
  shortcut?: string;
}

function Tool({ label, icon, onClick, disabled, pressed, busy, shortcut }: ToolProps) {
  return (
    <button
      type="button"
      className={`tool${pressed ? ' is-on' : ''}`}
      onClick={onClick}
      disabled={disabled || busy}
      aria-pressed={pressed}
      aria-busy={busy}
      title={shortcut ? `${label} (${shortcut})` : label}
    >
      {icon}
      <span className="tool-label">{label}</span>
    </button>
  );
}

interface ToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  notesMode: boolean;
  disabled: boolean;
  checking: boolean;
  hinting: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onNotes: () => void;
  onCheck: () => void;
  onHint: () => void;
}

export function Toolbar(p: ToolbarProps) {
  return (
    <div className="toolbar" role="toolbar" aria-label="Game tools">
      <Tool label="Undo" icon={<UndoIcon />} onClick={p.onUndo} disabled={p.disabled || !p.canUndo} shortcut="Ctrl+Z" />
      <Tool label="Redo" icon={<RedoIcon />} onClick={p.onRedo} disabled={p.disabled || !p.canRedo} shortcut="Ctrl+Y" />
      <Tool label="Notes" icon={<PencilIcon />} onClick={p.onNotes} pressed={p.notesMode} disabled={p.disabled} shortcut="N" />
      <Tool label="Check" icon={<CheckIcon />} onClick={p.onCheck} disabled={p.disabled} busy={p.checking} />
      <Tool label="Hint" icon={<HintIcon />} onClick={p.onHint} disabled={p.disabled} busy={p.hinting} />
    </div>
  );
}

interface NumberPadProps {
  counts: number[];
  /** Digits that currently repeat in a row, column or box. */
  clashDigits: Set<number>;
  notesMode: boolean;
  disabled: boolean;
  onDigit: (d: number) => void;
  onErase: () => void;
}

/** 1-9 and Erase, in two rows of five. */
export function NumberPad({ counts, clashDigits, notesMode, disabled, onDigit, onErase }: NumberPadProps) {
  return (
    <div className={`numpad${notesMode ? ' is-notes' : ''}`} aria-label={notesMode ? 'Add or remove notes' : 'Enter a number'}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => {
        const left = 9 - counts[d];
        const clash = clashDigits.has(d);
        const done = left <= 0 && !clash;
        return (
          <button
            type="button"
            key={d}
            className={`key${done ? ' is-done' : ''}${clash ? ' is-clash' : ''}`}
            onClick={() => onDigit(d)}
            disabled={disabled || (done && !notesMode)}
            aria-label={
              notesMode ? `Note ${d}` : `${d}, ${Math.max(left, 0)} left${clash ? ', repeated on the board' : ''}`
            }
          >
            <span className="key-digit">{d}</span>
            <span className="key-left" aria-hidden="true">{done || left < 0 ? '' : left}</span>
          </button>
        );
      })}
      <button type="button" className="key key-erase" onClick={onErase} disabled={disabled} aria-label="Erase" title="Erase (Backspace)">
        <EraseIcon width={24} height={24} />
        <span className="key-left">Erase</span>
      </button>
    </div>
  );
}

interface HintCardProps {
  hint: HintView;
  busy: boolean;
  onAnother: () => void;
  onReveal: () => void;
  onClose: () => void;
}

/** Shown in place of the toolbar while a hint is open; the number pad stays usable. */
export function HintCard({ hint, busy, onAnother, onReveal, onClose }: HintCardProps) {
  return (
    <section className="hint-card" aria-label="Hint" aria-live="polite">
      <button type="button" className="hint-close" onClick={onClose} aria-label="Close hint" title="Close (Esc)">
        <CloseIcon width={18} height={18} />
      </button>
      {hint.kind === 'step' ? (
        <>
          {hint.notice ? <p className="hint-notice">{hint.notice}</p> : null}
          <p className="hint-text">{hint.stage === 1 ? hint.step.nudge : hint.step.message}</p>
          <div className="hint-actions">
            <button type="button" className="btn small" onClick={onAnother} disabled={busy} aria-busy={busy}>
              Another hint
            </button>
            {hint.stage === 1 ? (
              <button type="button" className="btn small" onClick={onReveal}>
                Show me
              </button>
            ) : null}
            <button type="button" className="btn small primary" onClick={onClose}>
              Got it
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="hint-text">{hint.text}</p>
          <div className="hint-actions">
            <button type="button" className="btn small primary" onClick={onClose}>
              OK
            </button>
          </div>
        </>
      )}
    </section>
  );
}
